import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { ICON_SVGS } from '../utils/iconSvgs';
import {
  Template,
  TemplateElement,
  ImageElement,
  TextElement,
  ShapeElement,
  BrandAsset,
  AVAILABLE_FONTS,
  FontWeight,
  TextAlign,
  TextWrapStyle,
  TEXT_WRAP_STYLES,
  DynamicTextType,
  DYNAMIC_TEXT_LIMITS,
  EDITOR_CANVAS_FORMATS,
  EditorCanvasFormat,
  TemplateColorMode,
  POLYGON_PRESETS,
  PolygonPresetId,
  PolygonPoint,
  IconElement,
  IconName,
  ICON_LIBRARY,
} from '@agency-smm/shared';
import { apiClient } from '../services/apiClient';
import { useAuth } from '../hooks/useAuth';
import { Button } from '../components/Button';
import { PageSkeleton } from '../components/PageSkeleton';

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: calc(100vh - 80px);
  margin: -${({ theme }) => theme.spacing.lg};
`;

const TopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.lg};
  background-color: ${({ theme }) => theme.colors.surface};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const TemplateTitle = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
`;

const TitleInput = styled.input`
  font-size: ${({ theme }) => theme.fontSize.lg};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.colors.text};
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
  border-radius: 8px;

  &:hover,
  &:focus {
    background-color: ${({ theme }) => theme.colors.surfaceHover};
    outline: none;
  }
`;

const ToolbarActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const CanvasFormatSelect = styled.select`
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text};
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const ColorModeWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const ColorModeEditorSelect = styled.select<{ $mode: TemplateColorMode }>`
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
  border: 1px solid ${({ $mode }) =>
    $mode === 'brand' ? '#3B82F6' : $mode === 'adaptive' ? '#8B5CF6' : 'transparent'};
  border-radius: 8px;
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text};
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const ColorModeHint = styled.span`
  font-size: 10px;
  color: ${({ theme }) => theme.colors.textMuted};
  max-width: 140px;
  line-height: 1.2;
`;

const EditorLayout = styled.div`
  display: grid;
  grid-template-columns: 200px 1fr 280px;
  flex: 1;
  overflow: hidden;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const LeftSidebar = styled.div`
  background-color: ${({ theme }) => theme.colors.surface};
  border-right: 1px solid ${({ theme }) => theme.colors.border};
  overflow-y: auto;
  padding: ${({ theme }) => theme.spacing.md};

  @media (max-width: 1024px) {
    display: none;
  }
`;

const RightSidebar = styled.div`
  background-color: ${({ theme }) => theme.colors.surface};
  border-left: 1px solid ${({ theme }) => theme.colors.border};
  overflow-y: auto;
  padding: ${({ theme }) => theme.spacing.md};

  @media (max-width: 1024px) {
    display: none;
  }
`;

const CanvasArea = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ theme }) => theme.colors.background};
  padding: ${({ theme }) => theme.spacing.lg};
  overflow: auto;
`;

const CanvasWrapper = styled.div<{ $width: number; $height: number; $scale: number }>`
  position: relative;
  width: ${({ $width }) => $width}px;
  height: ${({ $height }) => $height}px;
  background-color: #1a1a2e;
  box-shadow: ${({ theme }) => theme.shadows.lg};
  overflow: hidden;
  transform: scale(${({ $scale }) => $scale});
  transform-origin: center center;
`;

const CanvasBackground = styled.div<{ $color: string }>`
  position: absolute;
  inset: 0;
  background-color: ${({ $color }) => $color === 'transparent' ? '#1a1a2e' : $color};
  background-image: ${({ $color }) =>
    $color === 'transparent'
      ? `linear-gradient(45deg, #2d2d44 25%, transparent 25%),
         linear-gradient(-45deg, #2d2d44 25%, transparent 25%),
         linear-gradient(45deg, transparent 75%, #2d2d44 75%),
         linear-gradient(-45deg, transparent 75%, #2d2d44 75%)`
      : 'none'};
  background-size: 20px 20px;
  background-position: 0 0, 0 10px, 10px -10px, -10px 0px;
`;

const ElementWrapper = styled.div<{
  $x: number;
  $y: number;
  $width: number;
  $height: number;
  $rotation: number;
  $opacity: number;
  $selected: boolean;
  $zIndex: number;
}>`
  position: absolute;
  left: ${({ $x }) => $x}%;
  top: ${({ $y }) => $y}%;
  width: ${({ $width }) => $width}%;
  height: ${({ $height }) => $height}%;
  transform: translate(-50%, -50%) rotate(${({ $rotation }) => $rotation}deg);
  opacity: ${({ $opacity }) => $opacity};
  z-index: ${({ $zIndex }) => $zIndex};
  cursor: move;
  outline: ${({ $selected, theme }) => ($selected ? `2px solid ${theme.colors.primary}` : 'none')};
  outline-offset: 2px;

  &:hover {
    outline: 2px solid ${({ theme }) => theme.colors.primary}80;
    outline-offset: 2px;
  }
`;

const ImageElementDisplay = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  pointer-events: none;
`;

const TextElementDisplay = styled.div<{
  $fontFamily: string;
  $fontSize: number;
  $fontWeight: string;
  $textColor: string;
  $textAlign: string;
  $autoSize?: boolean;
  $wrapStyle?: string;
  $outlineColor?: string;
  $outlineWidth?: number;
}>`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: ${({ $textAlign }) =>
    $textAlign === 'left' ? 'flex-start' : $textAlign === 'right' ? 'flex-end' : 'center'};
  font-family: ${({ $fontFamily }) => $fontFamily}, sans-serif;
  font-size: ${({ $fontSize }) => $fontSize}px;
  font-weight: ${({ $fontWeight }) => $fontWeight};
  color: ${({ $textColor }) => $textColor};
  text-align: ${({ $textAlign }) => $textAlign};
  white-space: ${({ $autoSize, $wrapStyle }) => {
    if ($wrapStyle === 'nowrap') return 'nowrap';
    return $autoSize !== false ? 'normal' : 'nowrap';
  }};
  word-wrap: ${({ $autoSize, $wrapStyle }) => {
    if ($wrapStyle === 'break-word') return 'break-word';
    if ($wrapStyle === 'break-all') return 'break-word';
    return $autoSize !== false ? 'break-word' : 'normal';
  }};
  word-break: ${({ $wrapStyle }) => ($wrapStyle === 'break-all' ? 'break-all' : 'normal')};
  overflow: ${({ $autoSize }) => ($autoSize !== false ? 'hidden' : 'visible')};
  pointer-events: none;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
  -webkit-text-stroke: ${({ $outlineWidth, $outlineColor }) =>
    $outlineWidth && $outlineWidth > 0 && $outlineColor
      ? `${$outlineWidth}px ${$outlineColor}`
      : 'none'};
  paint-order: stroke fill;
  line-height: 1.3;
`;

const ShapeElementDisplay = styled.div<{
  $backgroundColor: string;
  $borderRadius: number;
  $shapeType?: string;
  $skewAngle?: number;
  $clipPath?: string;
}>`
  width: 100%;
  height: 100%;
  background-color: ${({ $backgroundColor }) => $backgroundColor};
  border-radius: ${({ $borderRadius, $shapeType }) =>
    $shapeType === 'circle' || $shapeType === 'ellipse' ? '50%' : `${$borderRadius}px`};
  transform: ${({ $shapeType, $skewAngle }) =>
    $shapeType === 'parallelogram' && $skewAngle ? `skewX(${-$skewAngle}deg)` : 'none'};
  transform-origin: 50% 50%;
  clip-path: ${({ $clipPath }) => $clipPath || 'none'};
  -webkit-clip-path: ${({ $clipPath }) => $clipPath || 'none'};
  pointer-events: none;
`;


const SidebarSection = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const SectionTitle = styled.h3`
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

const AssetGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${({ theme }) => theme.spacing.xs};
`;

const AssetItem = styled.div`
  aspect-ratio: 1;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.colors.border};
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    transform: scale(1.02);
    box-shadow: ${({ theme }) => theme.shadows.sm};
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const AddElementButton = styled.button`
  width: 100%;
  padding: ${({ theme }) => theme.spacing.sm};
  border: 1px dashed ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  background: transparent;
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  margin-top: ${({ theme }) => theme.spacing.sm};

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.primary};
    box-shadow: ${({ theme }) => theme.shadows.sm};
  }
`;

const LayerItem = styled.div<{ $selected: boolean }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => theme.spacing.sm};
  border-radius: 8px;
  background-color: ${({ theme, $selected }) =>
    $selected ? theme.colors.surfaceHover : 'transparent'};
  border: ${({ $selected, theme }) => $selected ? `2px solid ${theme.colors.primary}` : '2px solid transparent'};
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    background-color: ${({ theme }) => theme.colors.surfaceHover};
  }
`;

const LayerIcon = styled.span`
  font-size: ${({ theme }) => theme.fontSize.lg};
`;

const LayerName = styled.span`
  flex: 1;
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.text};
`;

const DeleteLayerButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.textMuted};
  cursor: pointer;
  padding: 4px;
  opacity: 0;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  ${LayerItem}:hover & {
    opacity: 1;
  }

  &:hover {
    color: ${({ theme }) => theme.colors.error};
  }
`;

const PropertyGroup = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const PropertyLabel = styled.label`
  display: block;
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.colors.textMuted};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`;

const PropertyInput = styled.input`
  width: 100%;
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
  border: 2px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  font-size: ${({ theme }) => theme.fontSize.sm};
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text};

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const PropertySelect = styled.select`
  width: 100%;
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
  border: 2px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  font-size: ${({ theme }) => theme.fontSize.sm};
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text};
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const PropertyRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const ColorInput = styled.input`
  width: 100%;
  height: 36px;
  padding: 2px;
  border: 2px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  cursor: pointer;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing.lg};
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: ${({ theme }) => theme.fontSize.sm};
`;

const GlobalBanner = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.lg};
  background-color: ${({ theme }) => theme.colors.secondary};
  color: white;
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
`;

// Renders a lucide icon inline as styled SVG. Color comes from props so the
// editor preview matches the canvas render.
const IconElementDisplay = styled.div<{ $color: string }>`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  & > svg {
    width: 100%;
    height: 100%;
    stroke: ${({ $color }) => $color};
    color: ${({ $color }) => $color};
  }
`;

const LogoPlaceholder = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(128, 128, 128, 0.3);
  color: #999;
  font-size: 12px;
  font-weight: bold;
  pointer-events: none;
`;

const DynamicToggle = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => theme.spacing.sm};
  background-color: ${({ theme }) => theme.colors.background};
  border-radius: 8px;
  border: 2px solid ${({ theme }) => theme.colors.border};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

const ToggleLabel = styled.label`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.text};
  cursor: pointer;
`;

const ToggleSwitch = styled.input`
  width: 40px;
  height: 20px;
  appearance: none;
  background-color: ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  border: 2px solid ${({ theme }) => theme.colors.border};
  position: relative;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:checked {
    background-color: ${({ theme }) => theme.colors.primary};
  }

  &::before {
    content: '';
    position: absolute;
    width: 16px;
    height: 16px;
    border-radius: 8px;
    background-color: white;
    top: 0px;
    left: 0px;
    transition: transform 0.2s ease;
  }

  &:checked::before {
    transform: translateX(20px);
  }
`;

const DynamicBadge = styled.span`
  background: ${({ theme }) => theme.colors.primary};
  color: white;
  font-size: 10px;
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  padding: 2px 6px;
  border-radius: 8px;
  border: 2px solid ${({ theme }) => theme.colors.border};
`;

const DynamicTextPreview = styled.div`
  font-style: italic;
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: ${({ theme }) => theme.fontSize.xs};
  margin-top: ${({ theme }) => theme.spacing.xs};
`;

const PropertyTextarea = styled.textarea`
  width: 100%;
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
  border: 2px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  font-size: ${({ theme }) => theme.fontSize.sm};
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text};
  resize: vertical;
  min-height: 60px;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

// Phase 3: draggable vertex handle for polygon shapes and polygon clips.
// Positioned at a vertex's (x%, y%) of the element's bounding box.
const VertexHandle = styled.div<{ $x: number; $y: number; $kind: 'shape' | 'clip' }>`
  position: absolute;
  width: 14px;
  height: 14px;
  background-color: ${({ theme, $kind }) => ($kind === 'clip' ? '#ff9d00' : theme.colors.primary)};
  border: 2px solid white;
  border-radius: 50%;
  left: ${({ $x }) => $x}%;
  top: ${({ $y }) => $y}%;
  transform: translate(-50%, -50%);
  cursor: grab;
  box-shadow: 0 1px 3px rgba(0,0,0,0.4);
  z-index: 50;
  &:active {
    cursor: grabbing;
  }
`;

const ResizeHandle = styled.div<{ $position: string }>`
  position: absolute;
  width: 10px;
  height: 10px;
  background-color: ${({ theme }) => theme.colors.primary};
  border: 2px solid white;
  border-radius: 8px;
  cursor: ${({ $position }) => {
    switch ($position) {
      case 'nw':
      case 'se':
        return 'nwse-resize';
      case 'ne':
      case 'sw':
        return 'nesw-resize';
      default:
        return 'pointer';
    }
  }};
  ${({ $position }) => {
    switch ($position) {
      case 'nw':
        return 'top: -5px; left: -5px;';
      case 'ne':
        return 'top: -5px; right: -5px;';
      case 'sw':
        return 'bottom: -5px; left: -5px;';
      case 'se':
        return 'bottom: -5px; right: -5px;';
      default:
        return '';
    }
  }}
`;

// Helper to detect current canvas format based on dimensions
function detectCanvasFormat(width: number, height: number): EditorCanvasFormat | null {
  for (const [key, format] of Object.entries(EDITOR_CANVAS_FORMATS)) {
    if (format.width === width && format.height === height) {
      return key as EditorCanvasFormat;
    }
  }
  return null;
}

// Undo/Redo history hook
function useHistory<T>(initial: T | null) {
  const [past, setPast] = useState<T[]>([]);
  const [present, setPresent] = useState<T | null>(initial);
  const [future, setFuture] = useState<T[]>([]);

  const pushState = useCallback((newState: T) => {
    setPresent((prev) => {
      if (prev !== null) {
        setPast((p) => [...p.slice(-30), prev]); // Keep max 30 history entries
      }
      setFuture([]);
      return newState;
    });
  }, []);

  const setState = useCallback((newState: T | null) => {
    setPresent(newState);
  }, []);

  const undo = useCallback(() => {
    setPresent((currentPresent) => {
      if (past.length === 0) return currentPresent;
      const previous = past[past.length - 1];
      setPast((p) => p.slice(0, -1));
      if (currentPresent !== null) {
        setFuture((f) => [currentPresent, ...f]);
      }
      return previous;
    });
  }, [past]);

  const redo = useCallback(() => {
    setPresent((currentPresent) => {
      if (future.length === 0) return currentPresent;
      const next = future[0];
      setFuture((f) => f.slice(1));
      if (currentPresent !== null) {
        setPast((p) => [...p, currentPresent]);
      }
      return next;
    });
  }, [future]);

  return {
    state: present,
    setState,
    pushState,
    undo,
    redo,
    canUndo: past.length > 0,
    canRedo: future.length > 0,
  };
}

// Build a CSS clip-path polygon string from PolygonPoint[] (coords in 0..100).
// Returns undefined if points are invalid or missing.
function polygonPointsToClipPath(points: PolygonPoint[] | undefined): string | undefined {
  if (!points || points.length < 3) return undefined;
  const parts = points.map((p) => `${p.x}% ${p.y}%`).join(', ');
  return `polygon(${parts})`;
}

export function TemplateEditorPage() {
  const { workspaceId, templateId } = useParams<{ workspaceId: string; templateId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canvasRef = useRef<HTMLDivElement>(null);

  const {
    state: template,
    setState: setTemplate,
    pushState: pushTemplateState,
    undo: handleUndo,
    redo: handleRedo,
    canUndo,
    canRedo,
  } = useHistory<Template>(null);
  const [assets, setAssets] = useState<BrandAsset[]>([]);
  const [selectedElementId, setSelectedElementId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState<string | null>(null); // 'nw' | 'ne' | 'sw' | 'se' | null
  // Phase 3: interactive polygon vertex editing
  // Tracks which vertex index is being dragged, and whether it's a shape polygon or image clip polygon.
  const [vertexDrag, setVertexDrag] = useState<{ elementId: string; index: number; kind: 'shape' | 'clip' } | null>(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [resizeStart, setResizeStart] = useState({ x: 0, y: 0, width: 0, height: 0 });
  const [workspaceLogoUrl, setWorkspaceLogoUrl] = useState<string | null>(null);
  const [isDuplicating, setIsDuplicating] = useState(false);

  // Canvas display scale - visually scale down to fit in editor
  // The canvas is rendered at full resolution (e.g., 1080x1080) but scaled down visually
  const maxDisplayWidth = 500;
  const canvasScale = template ? Math.min(maxDisplayWidth / template.canvasWidth, maxDisplayWidth / template.canvasHeight) : 1;

  useEffect(() => {
    if (workspaceId && templateId) {
      fetchTemplate();
      fetchAssets();
      fetchWorkspaceLogo();
    }
  }, [workspaceId, templateId]);

  const fetchTemplate = async () => {
    try {
      setIsLoading(true);
      const data = await apiClient.get<Template>(
        `/api/workspaces/${workspaceId}/templates/${templateId}`
      );
      setTemplate(data);
    } catch (error) {
      console.error('Failed to fetch template:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAssets = async () => {
    try {
      const data = await apiClient.get<BrandAsset[]>(`/api/workspaces/${workspaceId}/assets`);
      setAssets(data);
    } catch (error) {
      console.error('Failed to fetch assets:', error);
    }
  };

  const fetchWorkspaceLogo = async () => {
    try {
      const logo = await apiClient.get<BrandAsset | null>(`/api/workspaces/${workspaceId}/logo`);
      if (logo) {
        setWorkspaceLogoUrl(logo.url);
      }
    } catch {
      // No logo set - that's fine
    }
  };

  const handleSave = async () => {
    if (!template) return;

    setIsSaving(true);
    try {
      await apiClient.put(`/api/workspaces/${workspaceId}/templates/${templateId}`, {
        name: template.name,
        description: template.description,
        backgroundColor: template.backgroundColor,
        canvasWidth: template.canvasWidth,
        canvasHeight: template.canvasHeight,
      });
    } catch (error) {
      console.error('Failed to save template:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddImageElement = async (asset: BrandAsset) => {
    if (!template) return;

    try {
      const updated = await apiClient.post<Template>(
        `/api/workspaces/${workspaceId}/templates/${templateId}/elements`,
        {
          elementType: 'image',
          assetId: asset.id,
          imageUrl: asset.url,
          name: asset.name,
        }
      );
      pushTemplateState(updated);
    } catch (error) {
      console.error('Failed to add image element:', error);
    }
  };

  const handleAddTextElement = async () => {
    if (!template) return;

    try {
      const updated = await apiClient.post<Template>(
        `/api/workspaces/${workspaceId}/templates/${templateId}/elements`,
        {
          elementType: 'text',
          textContent: 'Your Text',
        }
      );
      pushTemplateState(updated);
    } catch (error) {
      console.error('Failed to add text element:', error);
    }
  };

  const handleAddShapeElement = async () => {
    if (!template) return;

    try {
      const updated = await apiClient.post<Template>(
        `/api/workspaces/${workspaceId}/templates/${templateId}/elements`,
        {
          elementType: 'shape',
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          borderRadius: 0,
          name: 'Shape',
        }
      );
      pushTemplateState(updated);
    } catch (error) {
      console.error('Failed to add shape element:', error);
    }
  };

  const handleUpdateElement = async (elementId: string, updates: Record<string, unknown>) => {
    if (!template) return;

    try {
      const updated = await apiClient.put<Template>(
        `/api/workspaces/${workspaceId}/templates/${templateId}/elements/${elementId}`,
        updates
      );
      pushTemplateState(updated);
    } catch (error) {
      console.error('Failed to update element:', error);
    }
  };

  const handleDeleteElement = async (elementId: string) => {
    if (!template) return;

    try {
      const updated = await apiClient.delete<Template>(
        `/api/workspaces/${workspaceId}/templates/${templateId}/elements/${elementId}`
      );
      pushTemplateState(updated);
      if (selectedElementId === elementId) {
        setSelectedElementId(null);
      }
    } catch (error) {
      console.error('Failed to delete element:', error);
    }
  };

  const handleElementMouseDown = useCallback(
    (e: React.MouseEvent, element: TemplateElement) => {
      e.stopPropagation();
      setSelectedElementId(element.id);
      if (isReadOnly) return;
      setIsDragging(true);

      const canvasRect = canvasRef.current?.getBoundingClientRect();
      if (!canvasRect) return;

      const mouseX = ((e.clientX - canvasRect.left) / canvasRect.width) * 100;
      const mouseY = ((e.clientY - canvasRect.top) / canvasRect.height) * 100;

      setDragOffset({
        x: mouseX - element.transform.positionX,
        y: mouseY - element.transform.positionY,
      });
    },
    []
  );

  // Phase 3: start dragging a polygon vertex
  const handleVertexMouseDown = useCallback(
    (e: React.MouseEvent, element: TemplateElement, index: number, kind: 'shape' | 'clip') => {
      e.stopPropagation();
      setSelectedElementId(element.id);
      setVertexDrag({ elementId: element.id, index, kind });
    },
    []
  );

  const handleResizeMouseDown = useCallback(
    (e: React.MouseEvent, element: TemplateElement, handle: string) => {
      e.stopPropagation();
      setSelectedElementId(element.id);
      setIsResizing(handle);

      const canvasRect = canvasRef.current?.getBoundingClientRect();
      if (!canvasRect) return;

      const mouseX = ((e.clientX - canvasRect.left) / canvasRect.width) * 100;
      const mouseY = ((e.clientY - canvasRect.top) / canvasRect.height) * 100;

      setResizeStart({
        x: mouseX,
        y: mouseY,
        width: element.transform.width,
        height: element.transform.height,
      });
    },
    []
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!template || !canvasRef.current) return;

      const canvasRect = canvasRef.current.getBoundingClientRect();
      const mouseX = ((e.clientX - canvasRect.left) / canvasRect.width) * 100;
      const mouseY = ((e.clientY - canvasRect.top) / canvasRect.height) * 100;

      if (vertexDrag) {
        const element = template.elements.find((el) => el.id === vertexDrag.elementId);
        if (!element) return;

        // Convert canvas-% mouse position to element-local 0..100 coords
        const w = element.transform.width;
        const h = element.transform.height;
        if (w <= 0 || h <= 0) return;
        const elLeft = element.transform.positionX - w / 2;
        const elTop = element.transform.positionY - h / 2;
        const localX = ((mouseX - elLeft) / w) * 100;
        const localY = ((mouseY - elTop) / h) * 100;
        // Clamp to a reasonable range (allow slight overflow for expressive polygons)
        const clampedX = Math.max(-50, Math.min(150, localX));
        const clampedY = Math.max(-50, Math.min(150, localY));

        setTemplate({
          ...template,
          elements: template.elements.map((el) => {
            if (el.id !== vertexDrag.elementId) return el;
            if (vertexDrag.kind === 'shape' && el.elementType === 'shape') {
              const shape = el as ShapeElement;
              const pts = (shape.polygonPoints || []).map((p, i) =>
                i === vertexDrag.index ? { x: clampedX, y: clampedY } : p
              );
              return { ...shape, polygonPoints: pts };
            }
            if (vertexDrag.kind === 'clip' && el.elementType === 'image') {
              const img = el as ImageElement;
              const pts = (img.clipPolygonPoints || []).map((p, i) =>
                i === vertexDrag.index ? { x: clampedX, y: clampedY } : p
              );
              return { ...img, clipPolygonPoints: pts };
            }
            return el;
          }),
        });
        return;
      }

      if (isResizing && selectedElementId) {
        const element = template.elements.find((el) => el.id === selectedElementId);
        if (!element) return;

        const deltaX = mouseX - resizeStart.x;
        const deltaY = mouseY - resizeStart.y;

        let newWidth = resizeStart.width;
        let newHeight = resizeStart.height;

        // Calculate new dimensions based on which corner is being dragged
        if (isResizing === 'se') {
          newWidth = Math.max(5, resizeStart.width + deltaX);
          newHeight = Math.max(5, resizeStart.height + deltaY);
        } else if (isResizing === 'sw') {
          newWidth = Math.max(5, resizeStart.width - deltaX);
          newHeight = Math.max(5, resizeStart.height + deltaY);
        } else if (isResizing === 'ne') {
          newWidth = Math.max(5, resizeStart.width + deltaX);
          newHeight = Math.max(5, resizeStart.height - deltaY);
        } else if (isResizing === 'nw') {
          newWidth = Math.max(5, resizeStart.width - deltaX);
          newHeight = Math.max(5, resizeStart.height - deltaY);
        }

        setTemplate({
          ...template,
          elements: template.elements.map((el) =>
            el.id === selectedElementId
              ? { ...el, transform: { ...el.transform, width: newWidth, height: newHeight } }
              : el
          ),
        });
      } else if (isDragging && selectedElementId) {
        const newX = Math.max(0, Math.min(100, mouseX - dragOffset.x));
        const newY = Math.max(0, Math.min(100, mouseY - dragOffset.y));

        setTemplate({
          ...template,
          elements: template.elements.map((el) =>
            el.id === selectedElementId
              ? { ...el, transform: { ...el.transform, positionX: newX, positionY: newY } }
              : el
          ),
        });
      }
    },
    [isDragging, isResizing, selectedElementId, template, dragOffset, resizeStart, vertexDrag]
  );

  const handleMouseUp = useCallback(async () => {
    if (!template) {
      setIsDragging(false);
      setIsResizing(null);
      setVertexDrag(null);
      return;
    }

    // Persist vertex drag if one finished
    if (vertexDrag) {
      const element = template.elements.find((el) => el.id === vertexDrag.elementId);
      if (element) {
        if (vertexDrag.kind === 'shape' && element.elementType === 'shape') {
          await handleUpdateElement(element.id, {
            polygonPoints: (element as ShapeElement).polygonPoints,
          });
        } else if (vertexDrag.kind === 'clip' && element.elementType === 'image') {
          await handleUpdateElement(element.id, {
            clipPolygonPoints: (element as ImageElement).clipPolygonPoints,
          });
        }
      }
      setVertexDrag(null);
      setIsDragging(false);
      setIsResizing(null);
      return;
    }

    if (!selectedElementId) {
      setIsDragging(false);
      setIsResizing(null);
      return;
    }

    const element = template.elements.find((el) => el.id === selectedElementId);
    if (!element) {
      setIsDragging(false);
      setIsResizing(null);
      return;
    }

    if (isResizing) {
      await handleUpdateElement(selectedElementId, {
        width: element.transform.width,
        height: element.transform.height,
      });
    } else if (isDragging) {
      await handleUpdateElement(selectedElementId, {
        positionX: element.transform.positionX,
        positionY: element.transform.positionY,
      });
    }

    setIsDragging(false);
    setIsResizing(null);
  }, [isDragging, isResizing, selectedElementId, template, vertexDrag]);

  const handleCanvasClick = () => {
    setSelectedElementId(null);
  };

  const handleCanvasFormatChange = async (format: EditorCanvasFormat) => {
    if (!template) return;

    const { width, height } = EDITOR_CANVAS_FORMATS[format];

    setTemplate({
      ...template,
      canvasWidth: width,
      canvasHeight: height,
    });

    // Save immediately
    try {
      await apiClient.put(`/api/workspaces/${workspaceId}/templates/${templateId}`, {
        canvasWidth: width,
        canvasHeight: height,
      });
    } catch (error) {
      console.error('Failed to update canvas format:', error);
    }
  };

  const handleDuplicateGlobal = async () => {
    if (!template) return;
    setIsDuplicating(true);
    try {
      const duplicated = await apiClient.post<Template>(
        `/api/workspaces/${workspaceId}/templates/${templateId}/duplicate`,
        {}
      );
      navigate(`/workspace/${workspaceId}/templates/${duplicated.id}`);
    } catch (error) {
      console.error('Failed to duplicate template:', error);
    } finally {
      setIsDuplicating(false);
    }
  };

  const handleAddWorkspaceLogo = async () => {
    if (!template) return;

    try {
      const updated = await apiClient.post<Template>(
        `/api/workspaces/${workspaceId}/templates/${templateId}/elements`,
        {
          elementType: 'image',
          assetReferenceType: 'workspace_logo',
          name: 'Brand Logo',
          imageUrl: workspaceLogoUrl || undefined,
        }
      );
      pushTemplateState(updated);
    } catch (error) {
      console.error('Failed to add workspace logo element:', error);
    }
  };

  // Add a built-in icon element. See docs/template-editor/005-icon-library.md
  const handleAddIcon = async (iconName: IconName = 'star') => {
    if (!template) return;
    try {
      const updated = await apiClient.post<Template>(
        `/api/workspaces/${workspaceId}/templates/${templateId}/elements`,
        {
          elementType: 'icon',
          iconName,
          iconColor: '#000000',
          name: iconName,
          positionX: 50,
          positionY: 50,
          width: 6,
          height: 6,
        }
      );
      pushTemplateState(updated);
    } catch (error) {
      console.error('Failed to add icon element:', error);
    }
  };

  // Add a photo-zone element that resolves to the post's photo at render time.
  // See docs/template-editor/002-post-image-as-element.md
  const handleAddPostImage = async () => {
    if (!template) return;

    try {
      const updated = await apiClient.post<Template>(
        `/api/workspaces/${workspaceId}/templates/${templateId}/elements`,
        {
          elementType: 'image',
          assetReferenceType: 'post_image',
          name: 'Post Image',
          // Sensible default: upper half of the canvas
          positionX: 50,
          positionY: 30,
          width: 100,
          height: 50,
        }
      );
      pushTemplateState(updated);
    } catch (error) {
      console.error('Failed to add post image element:', error);
    }
  };

  const handleColorModeChange = async (mode: TemplateColorMode) => {
    if (!template) return;

    setTemplate({ ...template, colorMode: mode });

    try {
      await apiClient.put(`/api/workspaces/${workspaceId}/templates/${templateId}`, {
        colorMode: mode,
      });
    } catch (error) {
      console.error('Failed to update color mode:', error);
    }
  };

  const colorModeHints: Record<TemplateColorMode, string> = {
    fixed: 'Uses exact colors defined in the template',
    brand: 'Replaces colors with workspace brand colors',
    adaptive: 'AI picks colors based on post content',
  };

  // Keyboard shortcuts: Ctrl+Z (undo), Ctrl+Shift+Z/Ctrl+Y (redo), Delete (delete element)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (isReadOnlyRef.current) return;
      // Don't capture shortcuts when typing in inputs
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT') return;

      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        handleUndo();
      } else if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) {
        e.preventDefault();
        handleRedo();
      } else if (e.key === 'Delete' || e.key === 'Backspace') {
        if (selectedElementId) {
          e.preventDefault();
          handleDeleteElement(selectedElementId);
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleUndo, handleRedo, selectedElementId]);

  const isGlobalTemplate = template?.isGlobal || false;
  const isReadOnly = isGlobalTemplate && !user?.isAdmin;
  const isReadOnlyRef = useRef(isReadOnly);
  isReadOnlyRef.current = isReadOnly;
  const selectedElement = template?.elements.find((el) => el.id === selectedElementId);

  if (isLoading) {
    return <PageSkeleton />;
  }

  if (!template) {
    return <div>Template not found</div>;
  }

  return (
    <PageContainer>
      {isGlobalTemplate && user?.isAdmin && (
        <GlobalBanner style={{ backgroundColor: '#d97706' }}>
          <span>Editing global template (visible to all users)</span>
        </GlobalBanner>
      )}
      {isReadOnly && (
        <GlobalBanner>
          <span>This is a global template (read-only). Duplicate to customize.</span>
          <Button $size="sm" onClick={handleDuplicateGlobal} disabled={isDuplicating}>
            {isDuplicating ? 'Duplicating...' : 'Duplicate to Edit'}
          </Button>
        </GlobalBanner>
      )}
      <TopBar>
        <TemplateTitle>
          <Button $variant="outline" $size="sm" onClick={() => navigate(`/workspace/${workspaceId}/templates`)}>
            ← Back
          </Button>
          <TitleInput
            value={template.name}
            onChange={(e) => !isReadOnly && setTemplate({ ...template, name: e.target.value })}
            onBlur={() => !isReadOnly && handleSave()}
            readOnly={isReadOnly}
          />
          <CanvasFormatSelect
            value={detectCanvasFormat(template.canvasWidth, template.canvasHeight) || ''}
            onChange={(e) => !isReadOnly && handleCanvasFormatChange(e.target.value as EditorCanvasFormat)}
            disabled={isReadOnly}
          >
            {Object.entries(EDITOR_CANVAS_FORMATS).map(([key, format]) => (
              <option key={key} value={key}>
                {format.label}
              </option>
            ))}
          </CanvasFormatSelect>
          <ColorModeWrapper>
            <ColorModeEditorSelect
              $mode={(template.colorMode || 'fixed') as TemplateColorMode}
              value={template.colorMode || 'fixed'}
              onChange={(e) => !isReadOnly && handleColorModeChange(e.target.value as TemplateColorMode)}
              disabled={isReadOnly}
            >
              <option value="fixed">Fixed Colors</option>
              <option value="brand">Brand Colors</option>
              <option value="adaptive">Adaptive</option>
            </ColorModeEditorSelect>
            <ColorModeHint>
              {colorModeHints[(template.colorMode || 'fixed') as TemplateColorMode]}
            </ColorModeHint>
          </ColorModeWrapper>
        </TemplateTitle>
        <ToolbarActions>
          {!isReadOnly && (
            <>
              <Button $variant="outline" $size="sm" onClick={handleUndo} disabled={!canUndo} title="Undo (Ctrl+Z)">
                ↩
              </Button>
              <Button $variant="outline" $size="sm" onClick={handleRedo} disabled={!canRedo} title="Redo (Ctrl+Shift+Z)">
                ↪
              </Button>
              <Button $variant="outline" $size="sm" onClick={handleAddTextElement}>
                + Text
              </Button>
              <Button $variant="outline" $size="sm" onClick={handleAddShapeElement}>
                + Shape
              </Button>
              <Button $variant="outline" $size="sm" onClick={() => handleAddIcon('star')}>
                + Icon
              </Button>
              <Button onClick={handleSave} disabled={isSaving}>
                {isSaving ? 'Saving...' : 'Save'}
              </Button>
            </>
          )}
        </ToolbarActions>
      </TopBar>

      <EditorLayout>
        <LeftSidebar>
          {!isReadOnly && (
            <SidebarSection>
              <SectionTitle>Brand Assets</SectionTitle>
              {assets.length > 0 ? (
                <AssetGrid>
                  {assets.map((asset) => (
                    <AssetItem key={asset.id} onClick={() => handleAddImageElement(asset)}>
                      <img src={asset.url} alt={asset.name} />
                    </AssetItem>
                  ))}
                </AssetGrid>
              ) : (
                <EmptyState>No assets available</EmptyState>
              )}
              <AddElementButton onClick={handleAddWorkspaceLogo}>
                + Workspace Logo
              </AddElementButton>
              <AddElementButton onClick={handleAddPostImage}>
                + Post Image (photo zone)
              </AddElementButton>
              <AddElementButton onClick={() => navigate(`/workspace/${workspaceId}`)}>
                + Add Assets
              </AddElementButton>
            </SidebarSection>
          )}

          <SidebarSection>
            <SectionTitle>Layers</SectionTitle>
            {template.elements.length > 0 ? (
              [...template.elements]
                .sort((a, b) => b.zIndex - a.zIndex)
                .map((element) => (
                  <LayerItem
                    key={element.id}
                    $selected={element.id === selectedElementId}
                    onClick={() => setSelectedElementId(element.id)}
                  >
                    <LayerIcon>{element.elementType === 'image' ? '🖼' : element.elementType === 'shape' ? '■' : element.elementType === 'icon' ? '★' : 'T'}</LayerIcon>
                    <LayerName>
                      {element.name || element.elementType}
                      {element.elementType === 'text' && (element as TextElement).isDynamic && (
                        <DynamicBadge style={{ marginLeft: 6 }}>AI</DynamicBadge>
                      )}
                    </LayerName>
                    {!isReadOnly && (
                      <DeleteLayerButton onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteElement(element.id);
                      }}>
                        ×
                      </DeleteLayerButton>
                    )}
                  </LayerItem>
                ))
            ) : (
              <EmptyState>No elements yet</EmptyState>
            )}
          </SidebarSection>
        </LeftSidebar>

        <CanvasArea>
          <CanvasWrapper
            ref={canvasRef}
            $width={template.canvasWidth}
            $height={template.canvasHeight}
            $scale={canvasScale}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onClick={handleCanvasClick}
          >
            <CanvasBackground $color={template.backgroundColor} />
            {template.elements.map((element) => (
              <ElementWrapper
                key={element.id}
                $x={element.transform.positionX}
                $y={element.transform.positionY}
                $width={element.transform.width}
                $height={element.transform.height}
                $rotation={element.transform.rotation}
                $opacity={element.opacity}
                $selected={element.id === selectedElementId}
                $zIndex={element.zIndex}
                onMouseDown={(e) => handleElementMouseDown(e, element)}
              >
                {element.elementType === 'image' ? (
                  (() => {
                    const imgEl = element as ImageElement;
                    const resolvedUrl = imgEl.assetReferenceType === 'workspace_logo'
                      ? (workspaceLogoUrl || undefined)
                      : imgEl.assetReferenceType === 'post_image'
                      ? undefined  // editor shows placeholder; real photo fills at render time
                      : imgEl.imageUrl;
                    if (!resolvedUrl) {
                      return <LogoPlaceholder>
                        {imgEl.assetReferenceType === 'post_image' ? 'POST IMAGE' : 'LOGO'}
                      </LogoPlaceholder>;
                    }

                    let clipPath: string | undefined;
                    if (imgEl.clipShape === 'parallelogram' && imgEl.clipSkewAngle) {
                      const elW = (element.transform.width / 100) * template.canvasWidth;
                      const elH = (element.transform.height / 100) * template.canvasHeight;
                      if (elW > 0 && elH > 0) {
                        const halfOffsetPct =
                          ((elH / elW) * Math.tan((imgEl.clipSkewAngle * Math.PI) / 180) / 2) * 100;
                        clipPath = `polygon(${halfOffsetPct}% 0%, ${100 + halfOffsetPct}% 0%, ${100 - halfOffsetPct}% 100%, ${-halfOffsetPct}% 100%)`;
                      }
                    } else if (imgEl.clipShape === 'polygon') {
                      clipPath = polygonPointsToClipPath(imgEl.clipPolygonPoints);
                    }

                    return (
                      <ImageElementDisplay
                        src={resolvedUrl}
                        alt={element.name || 'Image'}
                        style={clipPath ? { clipPath, WebkitClipPath: clipPath } : undefined}
                      />
                    );
                  })()
                ) : element.elementType === 'shape' ? (
                  <ShapeElementDisplay
                    $backgroundColor={(element as ShapeElement).backgroundColor}
                    $borderRadius={(element as ShapeElement).borderRadius}
                    $shapeType={(element as ShapeElement).shapeType}
                    $skewAngle={(element as ShapeElement).skewAngle}
                    $clipPath={
                      (element as ShapeElement).shapeType === 'polygon'
                        ? polygonPointsToClipPath((element as ShapeElement).polygonPoints)
                        : undefined
                    }
                  />
                ) : element.elementType === 'icon' ? (
                  <IconElementDisplay
                    $color={(element as IconElement).iconColor || '#000000'}
                    dangerouslySetInnerHTML={{
                      __html: ICON_SVGS[(element as IconElement).iconName] || '',
                    }}
                  />
                ) : (
                  <TextElementDisplay
                    $fontFamily={(element as TextElement).fontFamily}
                    $fontSize={(element as TextElement).fontSize}
                    $fontWeight={(element as TextElement).fontWeight}
                    $textColor={(element as TextElement).textColor}
                    $textAlign={(element as TextElement).textAlign}
                    $autoSize={(element as TextElement).autoSize}
                    $wrapStyle={(element as TextElement).wrapStyle}
                    $outlineColor={(element as TextElement).outlineColor}
                    $outlineWidth={(element as TextElement).outlineWidth}
                  >
                    {(element as TextElement).isDynamic
                      ? `[AI: ${DYNAMIC_TEXT_LIMITS[(element as TextElement).dynamicType || 'headline'].label}]`
                      : (element as TextElement).textContent}
                  </TextElementDisplay>
                )}
                {element.id === selectedElementId && !isReadOnly && (
                  <>
                    <ResizeHandle $position="nw" onMouseDown={(e) => handleResizeMouseDown(e, element, 'nw')} />
                    <ResizeHandle $position="ne" onMouseDown={(e) => handleResizeMouseDown(e, element, 'ne')} />
                    <ResizeHandle $position="sw" onMouseDown={(e) => handleResizeMouseDown(e, element, 'sw')} />
                    <ResizeHandle $position="se" onMouseDown={(e) => handleResizeMouseDown(e, element, 'se')} />
                    {/* Polygon vertex handles — blue for shape, orange for image clip */}
                    {element.elementType === 'shape' && (element as ShapeElement).shapeType === 'polygon' && (element as ShapeElement).polygonPoints?.map((p, i) => (
                      <VertexHandle
                        key={`shape-vertex-${i}`}
                        $x={p.x}
                        $y={p.y}
                        $kind="shape"
                        onMouseDown={(e) => handleVertexMouseDown(e, element, i, 'shape')}
                      />
                    ))}
                    {element.elementType === 'image' && (element as ImageElement).clipShape === 'polygon' && (element as ImageElement).clipPolygonPoints?.map((p, i) => (
                      <VertexHandle
                        key={`clip-vertex-${i}`}
                        $x={p.x}
                        $y={p.y}
                        $kind="clip"
                        onMouseDown={(e) => handleVertexMouseDown(e, element, i, 'clip')}
                      />
                    ))}
                  </>
                )}
              </ElementWrapper>
            ))}
          </CanvasWrapper>
        </CanvasArea>

        <RightSidebar>
          {isReadOnly && selectedElement ? (
            <EmptyState>
              Properties are read-only for global templates. Duplicate to customize.
            </EmptyState>
          ) : selectedElement ? (
            <>
              <SidebarSection>
                <SectionTitle>Element Properties</SectionTitle>
                <PropertyGroup>
                  <PropertyLabel>Name</PropertyLabel>
                  <PropertyInput
                    value={selectedElement.name || ''}
                    onChange={(e) =>
                      setTemplate({
                        ...template,
                        elements: template.elements.map((el) =>
                          el.id === selectedElementId ? { ...el, name: e.target.value } : el
                        ),
                      })
                    }
                    onBlur={() => handleUpdateElement(selectedElement.id, { name: selectedElement.name })}
                  />
                </PropertyGroup>
              </SidebarSection>

              <SidebarSection>
                <SectionTitle>Position</SectionTitle>
                <PropertyRow>
                  <PropertyGroup>
                    <PropertyLabel>X (%)</PropertyLabel>
                    <PropertyInput
                      type="number"
                      value={Math.round(selectedElement.transform.positionX)}
                      onChange={(e) =>
                        setTemplate({
                          ...template,
                          elements: template.elements.map((el) =>
                            el.id === selectedElementId
                              ? { ...el, transform: { ...el.transform, positionX: Number(e.target.value) } }
                              : el
                          ),
                        })
                      }
                      onBlur={() =>
                        handleUpdateElement(selectedElement.id, {
                          positionX: selectedElement.transform.positionX,
                        })
                      }
                    />
                  </PropertyGroup>
                  <PropertyGroup>
                    <PropertyLabel>Y (%)</PropertyLabel>
                    <PropertyInput
                      type="number"
                      value={Math.round(selectedElement.transform.positionY)}
                      onChange={(e) =>
                        setTemplate({
                          ...template,
                          elements: template.elements.map((el) =>
                            el.id === selectedElementId
                              ? { ...el, transform: { ...el.transform, positionY: Number(e.target.value) } }
                              : el
                          ),
                        })
                      }
                      onBlur={() =>
                        handleUpdateElement(selectedElement.id, {
                          positionY: selectedElement.transform.positionY,
                        })
                      }
                    />
                  </PropertyGroup>
                </PropertyRow>
              </SidebarSection>

              <SidebarSection>
                <SectionTitle>Size</SectionTitle>
                <PropertyRow>
                  <PropertyGroup>
                    <PropertyLabel>Width (%)</PropertyLabel>
                    <PropertyInput
                      type="number"
                      value={Math.round(selectedElement.transform.width)}
                      onChange={(e) =>
                        setTemplate({
                          ...template,
                          elements: template.elements.map((el) =>
                            el.id === selectedElementId
                              ? { ...el, transform: { ...el.transform, width: Number(e.target.value) } }
                              : el
                          ),
                        })
                      }
                      onBlur={() =>
                        handleUpdateElement(selectedElement.id, {
                          width: selectedElement.transform.width,
                        })
                      }
                    />
                  </PropertyGroup>
                  <PropertyGroup>
                    <PropertyLabel>Height (%)</PropertyLabel>
                    <PropertyInput
                      type="number"
                      value={Math.round(selectedElement.transform.height)}
                      onChange={(e) =>
                        setTemplate({
                          ...template,
                          elements: template.elements.map((el) =>
                            el.id === selectedElementId
                              ? { ...el, transform: { ...el.transform, height: Number(e.target.value) } }
                              : el
                          ),
                        })
                      }
                      onBlur={() =>
                        handleUpdateElement(selectedElement.id, {
                          height: selectedElement.transform.height,
                        })
                      }
                    />
                  </PropertyGroup>
                </PropertyRow>
              </SidebarSection>

              <SidebarSection>
                <SectionTitle>Appearance</SectionTitle>
                <PropertyGroup>
                  <PropertyLabel>Opacity</PropertyLabel>
                  <PropertyInput
                    type="range"
                    min="0"
                    max="1"
                    step="0.1"
                    value={selectedElement.opacity}
                    onChange={(e) =>
                      setTemplate({
                        ...template,
                        elements: template.elements.map((el) =>
                          el.id === selectedElementId ? { ...el, opacity: Number(e.target.value) } : el
                        ),
                      })
                    }
                    onMouseUp={() =>
                      handleUpdateElement(selectedElement.id, { opacity: selectedElement.opacity })
                    }
                  />
                </PropertyGroup>
                <PropertyGroup>
                  <PropertyLabel>Z-Index</PropertyLabel>
                  <PropertyInput
                    type="number"
                    value={selectedElement.zIndex}
                    onChange={(e) =>
                      setTemplate({
                        ...template,
                        elements: template.elements.map((el) =>
                          el.id === selectedElementId ? { ...el, zIndex: Number(e.target.value) } : el
                        ),
                      })
                    }
                    onBlur={() =>
                      handleUpdateElement(selectedElement.id, { zIndex: selectedElement.zIndex })
                    }
                  />
                </PropertyGroup>
              </SidebarSection>

              {selectedElement.elementType === 'text' && (
                <SidebarSection>
                  <SectionTitle>Text Properties</SectionTitle>
                  <PropertyGroup>
                    <PropertyLabel>Text Content</PropertyLabel>
                    <PropertyInput
                      value={(selectedElement as TextElement).textContent}
                      onChange={(e) =>
                        setTemplate({
                          ...template,
                          elements: template.elements.map((el) =>
                            el.id === selectedElementId
                              ? { ...el, textContent: e.target.value }
                              : el
                          ),
                        })
                      }
                      onBlur={() =>
                        handleUpdateElement(selectedElement.id, {
                          textContent: (selectedElement as TextElement).textContent,
                        })
                      }
                    />
                  </PropertyGroup>
                  <PropertyGroup>
                    <PropertyLabel>Font Family</PropertyLabel>
                    <PropertySelect
                      value={(selectedElement as TextElement).fontFamily}
                      onChange={(e) => {
                        setTemplate({
                          ...template,
                          elements: template.elements.map((el) =>
                            el.id === selectedElementId
                              ? { ...el, fontFamily: e.target.value }
                              : el
                          ),
                        });
                        handleUpdateElement(selectedElement.id, { fontFamily: e.target.value });
                      }}
                    >
                      {AVAILABLE_FONTS.map((font) => (
                        <option key={font} value={font}>
                          {font}
                        </option>
                      ))}
                    </PropertySelect>
                  </PropertyGroup>
                  <PropertyRow>
                    <PropertyGroup>
                      <PropertyLabel>Font Size</PropertyLabel>
                      <PropertyInput
                        type="number"
                        value={(selectedElement as TextElement).fontSize}
                        onChange={(e) =>
                          setTemplate({
                            ...template,
                            elements: template.elements.map((el) =>
                              el.id === selectedElementId
                                ? { ...el, fontSize: Number(e.target.value) }
                                : el
                            ),
                          })
                        }
                        onBlur={() =>
                          handleUpdateElement(selectedElement.id, {
                            fontSize: (selectedElement as TextElement).fontSize,
                          })
                        }
                      />
                    </PropertyGroup>
                    <PropertyGroup>
                      <PropertyLabel>Weight</PropertyLabel>
                      <PropertySelect
                        value={(selectedElement as TextElement).fontWeight}
                        onChange={(e) => {
                          setTemplate({
                            ...template,
                            elements: template.elements.map((el) =>
                              el.id === selectedElementId
                                ? { ...el, fontWeight: e.target.value as FontWeight }
                                : el
                            ),
                          });
                          handleUpdateElement(selectedElement.id, { fontWeight: e.target.value });
                        }}
                      >
                        <option value="normal">Normal</option>
                        <option value="bold">Bold</option>
                        <option value="100">100</option>
                        <option value="200">200</option>
                        <option value="300">300</option>
                        <option value="400">400</option>
                        <option value="500">500</option>
                        <option value="600">600</option>
                        <option value="700">700</option>
                        <option value="800">800</option>
                        <option value="900">900</option>
                      </PropertySelect>
                    </PropertyGroup>
                  </PropertyRow>
                  <PropertyGroup>
                    <PropertyLabel>Text Color</PropertyLabel>
                    <ColorInput
                      type="color"
                      value={(selectedElement as TextElement).textColor}
                      onChange={(e) => {
                        setTemplate({
                          ...template,
                          elements: template.elements.map((el) =>
                            el.id === selectedElementId
                              ? { ...el, textColor: e.target.value }
                              : el
                          ),
                        });
                        handleUpdateElement(selectedElement.id, { textColor: e.target.value });
                      }}
                    />
                  </PropertyGroup>
                  <PropertyGroup>
                    <PropertyLabel>Alignment</PropertyLabel>
                    <PropertySelect
                      value={(selectedElement as TextElement).textAlign}
                      onChange={(e) => {
                        setTemplate({
                          ...template,
                          elements: template.elements.map((el) =>
                            el.id === selectedElementId
                              ? { ...el, textAlign: e.target.value as TextAlign }
                              : el
                          ),
                        });
                        handleUpdateElement(selectedElement.id, { textAlign: e.target.value });
                      }}
                    >
                      <option value="left">Left</option>
                      <option value="center">Center</option>
                      <option value="right">Right</option>
                    </PropertySelect>
                  </PropertyGroup>
                  <DynamicToggle>
                    <ToggleLabel>
                      Auto Size
                    </ToggleLabel>
                    <ToggleSwitch
                      type="checkbox"
                      checked={(selectedElement as TextElement).autoSize ?? true}
                      onChange={(e) => {
                        const autoSize = e.target.checked;
                        setTemplate({
                          ...template,
                          elements: template.elements.map((el) =>
                            el.id === selectedElementId
                              ? { ...el, autoSize }
                              : el
                          ),
                        });
                        handleUpdateElement(selectedElement.id, { autoSize });
                      }}
                    />
                  </DynamicToggle>
                  <DynamicTextPreview style={{ marginTop: '-8px' }}>
                    When enabled, text wraps and shrinks to fit the bounding box
                  </DynamicTextPreview>
                  <PropertyGroup>
                    <PropertyLabel>Wrap Style</PropertyLabel>
                    <PropertySelect
                      value={(selectedElement as TextElement).wrapStyle || 'normal'}
                      onChange={(e) => {
                        const wrapStyle = e.target.value as TextWrapStyle;
                        setTemplate({
                          ...template,
                          elements: template.elements.map((el) =>
                            el.id === selectedElementId
                              ? { ...el, wrapStyle }
                              : el
                          ),
                        });
                        handleUpdateElement(selectedElement.id, { wrapStyle });
                      }}
                    >
                      {TEXT_WRAP_STYLES.map((style) => (
                        <option key={style.value} value={style.value}>
                          {style.label}
                        </option>
                      ))}
                    </PropertySelect>
                  </PropertyGroup>
                </SidebarSection>
              )}

              {selectedElement.elementType === 'text' && (
                <SidebarSection>
                  <SectionTitle>Text Outline</SectionTitle>
                  <PropertyRow>
                    <PropertyGroup>
                      <PropertyLabel>Outline Color</PropertyLabel>
                      <ColorInput
                        type="color"
                        value={(selectedElement as TextElement).outlineColor || '#000000'}
                        onChange={(e) => {
                          setTemplate({
                            ...template,
                            elements: template.elements.map((el) =>
                              el.id === selectedElementId
                                ? { ...el, outlineColor: e.target.value }
                                : el
                            ),
                          });
                          handleUpdateElement(selectedElement.id, { outlineColor: e.target.value });
                        }}
                      />
                    </PropertyGroup>
                    <PropertyGroup>
                      <PropertyLabel>Width (px)</PropertyLabel>
                      <PropertyInput
                        type="number"
                        min="0"
                        max="10"
                        step="0.5"
                        value={(selectedElement as TextElement).outlineWidth || 0}
                        onChange={(e) =>
                          setTemplate({
                            ...template,
                            elements: template.elements.map((el) =>
                              el.id === selectedElementId
                                ? { ...el, outlineWidth: Number(e.target.value) }
                                : el
                            ),
                          })
                        }
                        onBlur={() =>
                          handleUpdateElement(selectedElement.id, {
                            outlineWidth: (selectedElement as TextElement).outlineWidth || 0,
                          })
                        }
                      />
                    </PropertyGroup>
                  </PropertyRow>
                  <DynamicTextPreview>
                    Set width to 0 to disable outline
                  </DynamicTextPreview>
                </SidebarSection>
              )}

              {selectedElement.elementType === 'text' && (
                <SidebarSection>
                  <SectionTitle>Text Enhancements</SectionTitle>
                  <PropertyRow>
                    <PropertyGroup>
                      <PropertyLabel>Letter Spacing</PropertyLabel>
                      <PropertyInput
                        type="number"
                        min="-2"
                        max="20"
                        step="0.5"
                        value={(selectedElement as TextElement).letterSpacing || 0}
                        onChange={(e) =>
                          setTemplate({
                            ...template,
                            elements: template.elements.map((el) =>
                              el.id === selectedElementId
                                ? { ...el, letterSpacing: Number(e.target.value) }
                                : el
                            ),
                          })
                        }
                        onBlur={() =>
                          handleUpdateElement(selectedElement.id, {
                            letterSpacing: (selectedElement as TextElement).letterSpacing || 0,
                          })
                        }
                      />
                    </PropertyGroup>
                    <PropertyGroup>
                      <PropertyLabel>Line Height</PropertyLabel>
                      <PropertyInput
                        type="number"
                        min="0.8"
                        max="3"
                        step="0.1"
                        value={(selectedElement as TextElement).lineHeight || 1.3}
                        onChange={(e) =>
                          setTemplate({
                            ...template,
                            elements: template.elements.map((el) =>
                              el.id === selectedElementId
                                ? { ...el, lineHeight: Number(e.target.value) }
                                : el
                            ),
                          })
                        }
                        onBlur={() =>
                          handleUpdateElement(selectedElement.id, {
                            lineHeight: (selectedElement as TextElement).lineHeight || 1.3,
                          })
                        }
                      />
                    </PropertyGroup>
                  </PropertyRow>
                </SidebarSection>
              )}

              {selectedElement.elementType === 'shape' && (
                <SidebarSection>
                  <SectionTitle>Shape Properties</SectionTitle>
                  <PropertyGroup>
                    <PropertyLabel>Shape Type</PropertyLabel>
                    <PropertySelect
                      value={(selectedElement as ShapeElement).shapeType || 'rectangle'}
                      onChange={(e) => {
                        const shapeType = e.target.value as ShapeElement['shapeType'];
                        // When switching TO polygon, seed with triangle so the shape is visible
                        const seededPolygon =
                          shapeType === 'polygon' && !(selectedElement as ShapeElement).polygonPoints
                            ? POLYGON_PRESETS['triangle'].points
                            : (selectedElement as ShapeElement).polygonPoints;
                        setTemplate({
                          ...template,
                          elements: template.elements.map((el) =>
                            el.id === selectedElementId
                              ? { ...el, shapeType, polygonPoints: seededPolygon }
                              : el
                          ),
                        });
                        handleUpdateElement(selectedElement.id, {
                          shapeType,
                          polygonPoints: seededPolygon,
                        });
                      }}
                    >
                      <option value="rectangle">Rectangle</option>
                      <option value="circle">Circle</option>
                      <option value="ellipse">Ellipse</option>
                      <option value="parallelogram">Parallelogram</option>
                      <option value="polygon">Polygon</option>
                    </PropertySelect>
                  </PropertyGroup>
                  {(selectedElement as ShapeElement).shapeType === 'polygon' && (
                    <PropertyGroup>
                      <PropertyLabel>Polygon Preset</PropertyLabel>
                      <PropertySelect
                        value=""
                        onChange={(e) => {
                          const presetId = e.target.value as PolygonPresetId;
                          if (!presetId || !POLYGON_PRESETS[presetId]) return;
                          const points = POLYGON_PRESETS[presetId].points;
                          setTemplate({
                            ...template,
                            elements: template.elements.map((el) =>
                              el.id === selectedElementId ? { ...el, polygonPoints: points } : el
                            ),
                          });
                          handleUpdateElement(selectedElement.id, { polygonPoints: points });
                        }}
                      >
                        <option value="">— Choose preset —</option>
                        {Object.entries(POLYGON_PRESETS).map(([id, { label }]) => (
                          <option key={id} value={id}>{label}</option>
                        ))}
                      </PropertySelect>
                    </PropertyGroup>
                  )}
                  <PropertyGroup>
                    <PropertyLabel>Background Color</PropertyLabel>
                    <PropertyInput
                      value={(selectedElement as ShapeElement).backgroundColor}
                      onChange={(e) =>
                        setTemplate({
                          ...template,
                          elements: template.elements.map((el) =>
                            el.id === selectedElementId
                              ? { ...el, backgroundColor: e.target.value }
                              : el
                          ),
                        })
                      }
                      onBlur={() =>
                        handleUpdateElement(selectedElement.id, {
                          backgroundColor: (selectedElement as ShapeElement).backgroundColor,
                        })
                      }
                      placeholder="rgba(0, 0, 0, 0.6) or #1B2838"
                    />
                  </PropertyGroup>
                  <PropertyGroup>
                    <PropertyLabel>Border Radius (px)</PropertyLabel>
                    <PropertyInput
                      type="number"
                      min="0"
                      max="50"
                      step="1"
                      value={(selectedElement as ShapeElement).borderRadius || 0}
                      onChange={(e) =>
                        setTemplate({
                          ...template,
                          elements: template.elements.map((el) =>
                            el.id === selectedElementId
                              ? { ...el, borderRadius: Number(e.target.value) }
                              : el
                          ),
                        })
                      }
                      onBlur={() =>
                        handleUpdateElement(selectedElement.id, {
                          borderRadius: (selectedElement as ShapeElement).borderRadius || 0,
                        })
                      }
                    />
                  </PropertyGroup>
                  {(selectedElement as ShapeElement).shapeType === 'parallelogram' && (
                    <PropertyGroup>
                      <PropertyLabel>Skew Angle ({Math.round((selectedElement as ShapeElement).skewAngle || 0)}°)</PropertyLabel>
                      <PropertyInput
                        type="range"
                        min="-45"
                        max="45"
                        step="1"
                        value={(selectedElement as ShapeElement).skewAngle || 0}
                        onChange={(e) =>
                          setTemplate({
                            ...template,
                            elements: template.elements.map((el) =>
                              el.id === selectedElementId
                                ? { ...el, skewAngle: Number(e.target.value) }
                                : el
                            ),
                          })
                        }
                        onMouseUp={() =>
                          handleUpdateElement(selectedElement.id, {
                            skewAngle: (selectedElement as ShapeElement).skewAngle || 0,
                          })
                        }
                        onTouchEnd={() =>
                          handleUpdateElement(selectedElement.id, {
                            skewAngle: (selectedElement as ShapeElement).skewAngle || 0,
                          })
                        }
                      />
                    </PropertyGroup>
                  )}
                </SidebarSection>
              )}

              {selectedElement.elementType === 'icon' && (
                <SidebarSection>
                  <SectionTitle>Icon Properties</SectionTitle>
                  <PropertyGroup>
                    <PropertyLabel>Icon</PropertyLabel>
                    <PropertySelect
                      value={(selectedElement as IconElement).iconName}
                      onChange={(e) => {
                        const iconName = e.target.value as IconName;
                        setTemplate({
                          ...template,
                          elements: template.elements.map((el) =>
                            el.id === selectedElementId ? { ...el, iconName } : el
                          ),
                        });
                        handleUpdateElement(selectedElement.id, { iconName });
                      }}
                    >
                      {ICON_LIBRARY.map(({ name, label, group }) => (
                        <option key={name} value={name}>{group} — {label}</option>
                      ))}
                    </PropertySelect>
                  </PropertyGroup>
                  <PropertyGroup>
                    <PropertyLabel>Color</PropertyLabel>
                    <PropertyInput
                      type="color"
                      value={(selectedElement as IconElement).iconColor || '#000000'}
                      onChange={(e) =>
                        setTemplate({
                          ...template,
                          elements: template.elements.map((el) =>
                            el.id === selectedElementId ? { ...el, iconColor: e.target.value } : el
                          ),
                        })
                      }
                      onBlur={() =>
                        handleUpdateElement(selectedElement.id, {
                          iconColor: (selectedElement as IconElement).iconColor,
                        })
                      }
                    />
                  </PropertyGroup>
                </SidebarSection>
              )}

              {selectedElement.elementType === 'image' && (
                <SidebarSection>
                  <SectionTitle>Image Clip</SectionTitle>
                  <PropertyGroup>
                    <PropertyLabel>Clip Shape</PropertyLabel>
                    <PropertySelect
                      value={(selectedElement as ImageElement).clipShape || 'rectangle'}
                      onChange={(e) => {
                        const clipShape = e.target.value as ImageElement['clipShape'];
                        // Seed polygon points when switching to polygon if none set
                        const seededPolygon =
                          clipShape === 'polygon' && !(selectedElement as ImageElement).clipPolygonPoints
                            ? POLYGON_PRESETS['trapezoid-right'].points
                            : (selectedElement as ImageElement).clipPolygonPoints;
                        setTemplate({
                          ...template,
                          elements: template.elements.map((el) =>
                            el.id === selectedElementId
                              ? { ...el, clipShape, clipPolygonPoints: seededPolygon }
                              : el
                          ),
                        });
                        handleUpdateElement(selectedElement.id, {
                          clipShape,
                          clipPolygonPoints: seededPolygon,
                        });
                      }}
                    >
                      <option value="rectangle">None (Rectangle)</option>
                      <option value="parallelogram">Parallelogram</option>
                      <option value="polygon">Polygon</option>
                    </PropertySelect>
                  </PropertyGroup>
                  {(selectedElement as ImageElement).clipShape === 'polygon' && (
                    <PropertyGroup>
                      <PropertyLabel>Clip Polygon Preset</PropertyLabel>
                      <PropertySelect
                        value=""
                        onChange={(e) => {
                          const presetId = e.target.value as PolygonPresetId;
                          if (!presetId || !POLYGON_PRESETS[presetId]) return;
                          const points = POLYGON_PRESETS[presetId].points;
                          setTemplate({
                            ...template,
                            elements: template.elements.map((el) =>
                              el.id === selectedElementId ? { ...el, clipPolygonPoints: points } : el
                            ),
                          });
                          handleUpdateElement(selectedElement.id, { clipPolygonPoints: points });
                        }}
                      >
                        <option value="">— Choose preset —</option>
                        {Object.entries(POLYGON_PRESETS).map(([id, { label }]) => (
                          <option key={id} value={id}>{label}</option>
                        ))}
                      </PropertySelect>
                    </PropertyGroup>
                  )}
                  {(selectedElement as ImageElement).clipShape === 'parallelogram' && (
                    <PropertyGroup>
                      <PropertyLabel>Clip Skew Angle ({Math.round((selectedElement as ImageElement).clipSkewAngle || 0)}°)</PropertyLabel>
                      <PropertyInput
                        type="range"
                        min="-45"
                        max="45"
                        step="1"
                        value={(selectedElement as ImageElement).clipSkewAngle || 0}
                        onChange={(e) =>
                          setTemplate({
                            ...template,
                            elements: template.elements.map((el) =>
                              el.id === selectedElementId
                                ? { ...el, clipSkewAngle: Number(e.target.value) }
                                : el
                            ),
                          })
                        }
                        onMouseUp={() =>
                          handleUpdateElement(selectedElement.id, {
                            clipSkewAngle: (selectedElement as ImageElement).clipSkewAngle || 0,
                          })
                        }
                        onTouchEnd={() =>
                          handleUpdateElement(selectedElement.id, {
                            clipSkewAngle: (selectedElement as ImageElement).clipSkewAngle || 0,
                          })
                        }
                      />
                    </PropertyGroup>
                  )}
                </SidebarSection>
              )}

              {selectedElement.elementType === 'text' && (
                <SidebarSection>
                  <SectionTitle>Dynamic Text (AI)</SectionTitle>
                  <DynamicToggle>
                    <ToggleLabel>
                      <DynamicBadge>AI</DynamicBadge>
                      Enable Dynamic Text
                    </ToggleLabel>
                    <ToggleSwitch
                      type="checkbox"
                      checked={(selectedElement as TextElement).isDynamic || false}
                      onChange={(e) => {
                        const isDynamic = e.target.checked;
                        setTemplate({
                          ...template,
                          elements: template.elements.map((el) =>
                            el.id === selectedElementId
                              ? {
                                  ...el,
                                  isDynamic,
                                  dynamicType: isDynamic ? 'headline' : undefined,
                                }
                              : el
                          ),
                        });
                        handleUpdateElement(selectedElement.id, {
                          isDynamic,
                          dynamicType: isDynamic ? 'headline' : null,
                        });
                      }}
                    />
                  </DynamicToggle>

                  {(selectedElement as TextElement).isDynamic && (
                    <>
                      <PropertyGroup>
                        <PropertyLabel>Text Type</PropertyLabel>
                        <PropertySelect
                          value={(selectedElement as TextElement).dynamicType || 'headline'}
                          onChange={(e) => {
                            const dynamicType = e.target.value as DynamicTextType;
                            setTemplate({
                              ...template,
                              elements: template.elements.map((el) =>
                                el.id === selectedElementId
                                  ? { ...el, dynamicType }
                                  : el
                              ),
                            });
                            handleUpdateElement(selectedElement.id, { dynamicType });
                          }}
                        >
                          {Object.entries(DYNAMIC_TEXT_LIMITS).map(([type, info]) => (
                            <option key={type} value={type}>
                              {info.label}
                            </option>
                          ))}
                        </PropertySelect>
                        <DynamicTextPreview>
                          AI will generate text based on post content
                        </DynamicTextPreview>
                      </PropertyGroup>

                      {(selectedElement as TextElement).dynamicType === 'custom' && (
                        <PropertyGroup>
                          <PropertyLabel>Custom Prompt</PropertyLabel>
                          <PropertyTextarea
                            placeholder="Describe what kind of text you want the AI to generate..."
                            value={(selectedElement as TextElement).dynamicPrompt || ''}
                            onChange={(e) =>
                              setTemplate({
                                ...template,
                                elements: template.elements.map((el) =>
                                  el.id === selectedElementId
                                    ? { ...el, dynamicPrompt: e.target.value }
                                    : el
                                ),
                              })
                            }
                            onBlur={() =>
                              handleUpdateElement(selectedElement.id, {
                                dynamicPrompt: (selectedElement as TextElement).dynamicPrompt,
                              })
                            }
                          />
                        </PropertyGroup>
                      )}
                    </>
                  )}
                </SidebarSection>
              )}

              <Button
                $variant="outline"
                $fullWidth
                onClick={() => handleDeleteElement(selectedElement.id)}
                style={{ marginTop: 'auto', color: '#ef4444' }}
              >
                Delete Element
              </Button>
            </>
          ) : (
            <EmptyState>Select an element to edit its properties</EmptyState>
          )}
        </RightSidebar>
      </EditorLayout>
    </PageContainer>
  );
}
